define('viewModels/privileged/qm/RecordingManagerView',[
    'lib/knockout',
    'lib/lodash4',
    'lib/bluebird',
    'jquery',
    'moment',
    'util/ininAudioUtils',
    'util/errorCallbackFactory',
    'services/authService',
    'services/loggingService',
    'services/notificationService',
    'services/qm/qualityAuth',
    'services/qualityManagementService',
    'services/voicemailService',
    'viewModels/privileged/qm/util/FileStateEnum',
    'viewModels/privileged/qm/RecordingView',
    'viewModels/privileged/qm/AcdVoicemailView',
    'viewModels/privileged/qm/MessagesRecordingView',
    'viewModels/privileged/qm/ScreenRecordingDetailsView',
    'viewModels/ViewModel'
], function (ko,
    _,
    Promise,
    $,
    moment,
    ininAudioUtils,
    errorCallbackFactory,
    authService,
    LOG,
    notificationService,
    qualityAuthorization,
    qualityManagementService,
    voicemailService,
    FileStateEnum,
    RecordingView,
    AcdVoicemailView,
    MessagesRecordingView,
    ScreenRecordingDetailsView,
    ViewModel) {
        "use strict";

        var RecordingManagerView = ViewModel.extend(function (conversationId, acdVoicemailInfo, options) {
            var self = this;
            var failsafeTimer = null;
            var failsafeTimeoutMs = 5 * 60 * 60 * 1000; //5 minutes
            var recordingRequestTimeout;
            var recordingPromises = null;
            var maxReloads = 3;

            self.hasEmail = ko.observable(false);
            self.hasMessages = ko.observable(false);
            self.conversation = ko.observable();
            self.conversationId = ko.isObservable(conversationId) ? conversationId : ko.observable(conversationId);
            self.isRecordingLoading = ko.observable(true);
            self.showLongRunningMessage = ko.observable(false);
            self.recordingRequestError = ko.observable();
            self.conversationRecordingInformation = ko.observableArray();
            self.vmRecordingInformation = ko.observableArray();
            self.conversationRecordingViews = ko.observableArray();
            self.vmRecordingViews = ko.observableArray();
            self.recordingViews = ko.observableArray();
            self.playbackContext = ko.observable();
            self.recordingInformation = ko.observableArray();
            self.screenRecordingDetailsView = ko.observable();

            self.subscribe(self.conversationId, function (newConvId) {
                self.recordingViews(null);
                recordingItemContainer(newConvId, true);
            });

            if (options && options.playbackContext) {
                self.playbackContext(options.playbackContext);
            }

            self.keywordSpots = self.computed(function () {
                return _.flatMap(ko.unwrap(self.recordingViews), function (recordingView) {
                    return ko.unwrap(recordingView.keywordSpots) || [];
                });
            });

            var handleRecordingUpdates = function (incomingPayload) {
                //If the incoming payload is from playback BUT we did not have a 202 recording, we don't want to call playback again.
                if (!recordingPromises && incomingPayload && !(incomingPayload.actualTranscodeTimeMs && !self.showLongRunningMessage())) {
                    LOG.info("Requesting recordings for conversation after CP event.", { conversation: self.conversationId() });
                    recordingItemContainer(self.conversationId(), false);
                }
            };

            // polling failsafe. Begin polling if we get a long transcode.
            // This is needed because the event doesn't have all the information, and sometimes it is possible for the event
            // to come, and then the API returns with a 202. At that point, we have to poll until we have consistency.
            var pollForRecordings = function (initialTimeoutMs) {
                recordingRequestTimeout = setTimeout(function () {
                    if (self.showLongRunningMessage()) {
                        // If we're waiting on a request already, don't do it again.
                        if (!recordingPromises && self.conversationId()) {
                            LOG.info("Requesting recordings for conversation after waiting " + initialTimeoutMs.toString() + "ms", { conversation: self.conversationId() });
                            recordingItemContainer(self.conversationId(), false);
                        }
                        // Back off the rate at which we poll
                        pollForRecordings(initialTimeoutMs + initialTimeoutMs);
                    }
                }, initialTimeoutMs);
            };

            var recordingItemContainer = function (conversationId, shouldSubscribe) {
                var formatId = ininAudioUtils.getSuggestedFormatId();

                //This is passed down to the AudioPlayerView so it can force a reload if we have an unexpected playback
                //error (such as happens when the cached items get deleted out from under it)
                var reloadCallback = function () {
                    if (maxReloads-- > 0) {
                        self.isRecordingLoading(true);
                    }
                    getRecordings(conversationId);
                };

                var buildAcdVoicemailViews = function (ids) {
                    self.vmRecordingViews(_.map(ids, function (voicemailId) {
                        return self.subView(AcdVoicemailView, voicemailId);
                    }));

                    self.vmRecordingInformation(_.map(ids, function (voicemailId, index) {
                        // AcdVoicemailView services as viewmodel for top and bottom parts
                        return self.vmRecordingViews()[index];
                    }));
                };

                var getRecordings = function (conversationId) {
                    recordingPromises = [];
                    if (acdVoicemailInfo && acdVoicemailInfo.acdVoicemailIds) {
                        var canViewAcdVoicemails = (authService.hasAnyAuthorization(['perm:voicemail:acdVoicemail:view']) ||
                            (acdVoicemailInfo.isCurrentUserOnConversation && acdVoicemailInfo.isCurrentUserOnConversation()));

                        if (canViewAcdVoicemails) {
                            if (acdVoicemailInfo.acdVoicemailIds()) {
                                buildAcdVoicemailViews(acdVoicemailInfo.acdVoicemailIds());
                            } else {
                                var notifyHasVoicemailIds;
                                recordingPromises.push(new Promise(function(resolve) {
                                    notifyHasVoicemailIds = resolve;
                                }));

                                self.subscribe(acdVoicemailInfo.acdVoicemailIds, function () {
                                    buildAcdVoicemailViews(acdVoicemailInfo.acdVoicemailIds());

                                    if (notifyHasVoicemailIds) {
                                        notifyHasVoicemailIds(true);
                                        notifyHasVoicemailIds = null;
                                    }
                                });
                            }
                        } else {
                            LOG.info("Not requesting acdVoicemails", {
                                isCurrentUserOnConversation: acdVoicemailInfo.isCurrentUserOnConversation(),
                                hasViewAuthorization: authService.hasAnyAuthorization(['perm:voicemail:acdVoicemail:view']),
                            });
                        }
                    }

                    if (qualityAuthorization.canViewRecording()) {
                        recordingPromises.push(qualityManagementService.getRecordings(conversationId, formatId)
                            .then(function (recordingModels) {
                                //If any are long running (beyond the API call), just wait for the CP event
                                //(which was set below, before the API call)
                                if (isLongRunning(recordingModels)) {
                                    // If we already got recording data back, don't overwrite it with long running
                                    if (self.conversationRecordingViews().length === 0) {
                                        self.showLongRunningMessage(true);
                                        self.isRecordingLoading(true);
                                        if (!recordingRequestTimeout) {
                                            // Start with big timeout. This it not really meant to be responsive.
                                            // It is a failsafe.
                                            pollForRecordings(5000);
                                        }
                                    }
                                } else {
                                    //Clear the failsafe timer
                                    if (failsafeTimer) {
                                        clearTimeout(failsafeTimer);
                                        failsafeTimer = null;
                                    }

                                    if (self.showLongRunningMessage()) {
                                        self.showLongRunningMessage(false);
                                    }

                                    if (recordingModels && _.find(recordingModels, function (rec) {
                                        return !!rec.emailTranscript || rec.media === 'email';
                                    })) {
                                        self.hasEmail(true);
                                    }

                                    if (recordingModels && _.find(recordingModels, function (rec) {
                                        return !!rec.messageTranscript || rec.media === 'message';
                                    })) {
                                        self.hasMessages(true);
                                    }

                                    // Handle message recordings separately
                                    var messageModels = [];
                                    var screenModels = [];
                                    var sortedModels = [];
                                    _.each(recordingModels, function (recordingModel) {
                                        if (recordingModel.media === 'message') {
                                            messageModels.push(recordingModel);
                                        } else if (recordingModel.media === 'screen') {
                                            screenModels.push(recordingModel);
                                        } else {
                                            sortedModels.push(recordingModel);
                                        }
                                    });

                                    // Sort screen recordings to the end
                                    sortedModels = _.sortBy(sortedModels, function (recordingModel) {
                                        return recordingModel.media === 'screen';
                                    });

                                    var recordingViews = _.map(sortedModels, function (recordingModel, index) {
                                        var temp = {
                                            reloadCallback: reloadCallback,
                                            isLast: index === sortedModels.length - 1
                                        };
                                        if (_.isNil(recordingModel.media) && self.hasEmail() && recordingModel.fileState === FileStateEnum.DELETED) {
                                            recordingModel.media = 'email';
                                        }
                                        return self.subView(RecordingView, conversationId, recordingModel, temp, self.conversation);
                                    });

                                    var messagesRecordingView;
                                    if (messageModels.length) {
                                        messagesRecordingView = self.subView(MessagesRecordingView, conversationId, messageModels, self.conversation);
                                        recordingViews.splice(0, 0, messagesRecordingView);
                                    }

                                    if (screenModels.length) {
                                        var screenRecordingDetailsView = self.subView(ScreenRecordingDetailsView, window.parent.location.href);
                                        var screenRecordingView = self.subView(RecordingView, conversationId, screenModels[0], {}, self.conversation);
                                        self.screenRecordingDetailsView(screenRecordingDetailsView);
                                        recordingViews.push(screenRecordingView);
                                    }

                                    self.conversationRecordingViews(recordingViews);

                                    if (!recordingViews.length) {
                                        //initialize empty recording
                                        self.conversationRecordingViews([self.subView(RecordingView, conversationId, ko.observable({}), { reloadCallback: reloadCallback, displayed: true }, self.conversation)]);
                                    }

                                    //since this really doesn't affect recordings, this is placed after the isLoading part
                                    var recordingInformation = _.map(sortedModels, function (recordingModel, index) {
                                        return {
                                            recordingContext: self.playbackContext(),
                                            media: recordingModel.media,
                                            recordingFileState: recordingModel.fileState,
                                            recordingId: recordingModel.id,
                                            conversationId: recordingModel.conversationId,
                                            retentionInformation: {
                                                archiveDate: recordingModel.archiveDate,
                                                deleteDate: recordingModel.deleteDate,
                                                exportedDate: recordingModel.exportedDate
                                            },
                                            isDisplayed: recordingViews[index].displayed,
                                            isDeleted: recordingViews[index].isDeleted
                                        };
                                    });

                                    //There are many message recording models, but only one recordingView for them so don't index against recordingViews
                                    recordingInformation = recordingInformation.concat(_.map(messageModels, function (recordingModel) {
                                        return {
                                            recordingContext: self.playbackContext(),
                                            media: recordingModel.media,
                                            recordingFileState: recordingModel.fileState,
                                            recordingId: recordingModel.id,
                                            conversationId: recordingModel.conversationId,
                                            retentionInformation: {
                                                archiveDate: recordingModel.archiveDate,
                                                deleteDate: recordingModel.deleteDate,
                                                exportedDate: recordingModel.exportedDate
                                            },
                                            isDisplayed: messagesRecordingView.displayed,
                                            isDeleted: recordingModel.fileState && recordingModel.fileState.toLowerCase() === "deleted"
                                        };
                                    }));

                                    self.conversationRecordingInformation(recordingInformation);
                                }
                            }).catch(function (error) {
                                self.recordingRequestError(true);
                                var httpStatus = error.type === "http" ? error.details.status : undefined;
                                // A 404 indicates the recording doesn't exist. That's ok.
                                if (!(httpStatus && httpStatus === 404)) {
                                    // It's not an http error or a 404
                                    errorCallbackFactory.createCallback({ namespace: "playback.recordingRequest" })(error);
                                    throw error;
                                } else if (!options || (options.isActive && !options.isActive())) {
                                    //initialize empty recording
                                    self.conversationRecordingViews([self.subView(RecordingView, conversationId, ko.observable({}), { reloadCallback: reloadCallback, displayed: true }, self.conversation)]);
                                }
                            }));
                    }

                    Promise.all(recordingPromises)
                    .catch(function () {
                        self.recordingRequestError(true);
                    })
                    .finally(function () {
                        recordingPromises = null;
                        var conversationRecordingViews = self.conversationRecordingViews();
                        var vmRecordingViews = self.vmRecordingViews();
                        self.recordingViews(vmRecordingViews.concat(conversationRecordingViews));
                        if (self.recordingViews().length > 0) {
                            updateCounterDisplay(0);
                        }

                        var conversationRecordingInformation = self.conversationRecordingInformation();
                        var vmRecordingInformation = self.vmRecordingInformation();
                        self.recordingInformation(vmRecordingInformation.concat(conversationRecordingInformation));
                        if (!self.showLongRunningMessage()) {
                            self.isRecordingLoading(false);
                        }
                    });
                };

                //The .then() following the notificationService.subscribe is not called on
                //a second pass (after receiving a transcode completed event)... skip the
                //subscribe in this case and just call getRecordings directly.
                if (shouldSubscribe) {
                    var topic = 'conversations.' + conversationId + '.recordings';
                    notificationService.subscribe(topic, handleRecordingUpdates)
                        .catch(errorCallbackFactory.createCallback({ namespace: "playback.recordingNotifications" }))
                        .then(function () {
                            getRecordings(conversationId);

                            //Just in case we run long and then also never get a CP transcode complete event,
                            // set up a timer to clean up after a while
                            failsafeTimer = _.delay(function () {

                                self.isRecordingLoading(false);
                                self.recordingRequestError(true);
                                LOG.error("Transcode failsafe timer fired for conversation ", { conversation: self.conversationId() });

                            }, failsafeTimeoutMs);


                        });
                } else {
                    getRecordings(conversationId);
                }
            };

            LOG.info("Requesting recordings for conversation " + self.conversationId() + " on page load.");
            recordingItemContainer(self.conversationId(), true);

            var isLongRunning = function (recordingModels) {
                if (!recordingModels) {
                    return true;
                }
                //If any are still not done, wait for the rest to finish
                var anyLongRunning = false;
                _.each(recordingModels, function (recordingModel) {
                    if (recordingModel.estimatedTranscodeTimeMs && !recordingModel.actualTranscodeTimeMs) {
                        anyLongRunning = true;
                    }
                });
                return anyLongRunning;
            };

            var counter = ko.observable(0);
            var updateCounterDisplay = function (newIndex) {
                _.each(self.recordingViews(), function(view, index) {
                    view.displayed(index === newIndex);
                });
                counter(newIndex);
                self.recordingViews()[counter()].displayed(true);
            };

            self.isVoicemailViewSelected = self.computed(function () {
                if (self.recordingViews().length > counter()) {
                    return !!self.recordingViews()[counter()].isVoicemail;
                }
                return false;
            });

            var pauseCurrentRecording = function () {
                if (self.recordingViews()[counter()].pause) {
                    self.recordingViews()[counter()].pause();
                }
            };

            self.nextRecording = function () {
                pauseCurrentRecording();
                if (counter() < self.recordingViews().length - 1) {
                    updateCounterDisplay(counter() + 1);
                }
            };

            self.previous = function () {
                pauseCurrentRecording();
                if (counter() > 0) {
                    updateCounterDisplay(counter() - 1);
                }
            };

            self.getRecording = function (index) {
                pauseCurrentRecording();
                if (self.recordingViews()[index]) {
                    updateCounterDisplay(index);
                }
            };

            self.setConversation = function (conversation) {
                self.conversation = conversation;
            };

            self.hasPrevious = function () {
                return counter() > 0;
            };

            self.hasNext = function () {
                return counter() < self.recordingViews().length - 1;
            };

            self.selectedRecording = ko.computed(function () {
                if (self.recordingViews()) {
                    if (self.recordingViews()[counter()]) {
                        return self.recordingViews()[counter()];
                    }
                }
                return null;
            });

            self.saveAnnotation = function () {
                if (self.recordingViews()[counter()]) {
                    self.recordingViews()[counter()].saveAnnotation();
                }
            };

            self.hasRecordings = function () {
                return !_.isEmpty(self.recordingViews());
            };

            self.hasScreenRecordings = self.computed(function () {
                return self.screenRecordingDetailsView();
            });

            /**
             * CLEAN UP
             */
            self.onDispose = function () {
                clearTimeout(recordingRequestTimeout);
            };
        });

        return RecordingManagerView;
    });

